import { useCallback, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { z } from 'zod';
import type { RxCollection } from 'rxdb';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { type GridApiPro, type GridValidRowModel } from '../lib/dataGrid';
import ActiveToolButton from './ActiveToolButton';
import { useToast } from './toastContext';
import GridBulkEditDialog from './GridBulkEditDialog';
import {
    bulkFieldIssues,
    inlineStoredValues,
    useBulkFieldUpdate,
    type InlineField
} from './gridInlineEdit';

/**
 * The Sammelbearbeitung, wired to one grid: the toolbar button, the dialog and
 * the write.
 *
 * A hook rather than a component because the two halves live in different
 * places — the button in the toolbar's `filterControls` slot, the dialog next
 * to the grid — and both need the same selection.
 *
 * The fields on offer are the grid's OWN inline-edit fields, so there is no
 * second list to keep in step.
 */

/**
 * The record behind a selected row, or null when the row is not one. The
 * Befunde grid is a TREE whose Planum and Profil rows hold none of these
 * fields — null keeps them out of the count and out of the write.
 */
export type BulkDocOf<Row, Doc> = (row: Row) => (Doc & { id: string }) | null;

/**
 * `Row` is what the GRID holds, `Doc` what the DATABASE holds — the same thing
 * in a flat grid, different in the Befund tree, whose rows are a display shape
 * carrying their Befund alongside. Hence collection, schema and `docOf` all
 * speak of the document.
 */
export interface GridBulkEditOptions<
    Row extends GridValidRowModel,
    Doc extends GridValidRowModel
> {
    apiRef: React.RefObject<GridApiPro | null>;
    collection: RxCollection<Doc> | null;
    /** The same list the grid's editable columns are built from. */
    fields: readonly InlineField[];
    /** The record schema the form validates against. */
    schema: z.ZodType<unknown>;
    canEdit: boolean;
    /** The i18n id of a field's label, e.g. `labelIdsFrom('find.field')`. */
    labelId: (name: string) => string;
    /** Counts the records in words: "23 Funde bearbeiten". */
    titleId: string;
    /** Likewise for the toast afterwards: "23 Funde geändert." */
    doneId: string;
    docOf: BulkDocOf<Row, Doc>;
}

export function useGridBulkEdit<
    Row extends GridValidRowModel,
    Doc extends GridValidRowModel = Row
>({
    apiRef,
    collection,
    fields,
    schema,
    canEdit,
    labelId,
    titleId,
    doneId,
    docOf
}: GridBulkEditOptions<Row, Doc>): {
    button: ReactNode;
    dialog: ReactNode;
    /** Hand to the grid's `onRowSelectionModelChange`. */
    onSelectionChange: () => void;
} {
    const intl = useIntl();
    const { notify } = useToast();
    const [open, setOpen] = useState(false);
    // Bumped with every opening and used as the dialog's key, so each run starts
    // from a fresh mount instead of the dialog wiping its own state on `open`.
    const [opening, setOpening] = useState(0);
    const [count, setCount] = useState(0);
    const applyBulk = useBulkFieldUpdate<Doc>(collection, fields);

    const selectedDocs = useCallback(() => {
        const api = apiRef.current;
        if (!api) {
            return [];
        }
        return [...api.getSelectedRows().values()]
            .map((row) => docOf(row as Row))
            .filter((doc): doc is Doc & { id: string } => Boolean(doc));
    }, [apiRef, docOf]);

    const onSelectionChange = useCallback(
        () => setCount(selectedDocs().length),
        [selectedDocs]
    );

    if (!canEdit) {
        return { button: null, dialog: null, onSelectionChange };
    }

    const label = intl.formatMessage({ id: 'grid.bulkEdit.button' });
    return {
        onSelectionChange,
        button: (
            <Tooltip title={label}>
                {/* A disabled button inside a Tooltip needs a focusable
                    wrapper for the tooltip to appear on keyboard focus. */}
                <span>
                    <ActiveToolButton
                        aria-label={label}
                        active={count > 0}
                        disabled={count === 0}
                        onClick={() => {
                            setOpening((count) => count + 1);
                            setOpen(true);
                        }}
                    >
                        <Badge badgeContent={count} color="info">
                            <EditNoteIcon fontSize="small" />
                        </Badge>
                    </ActiveToolButton>
                </span>
            </Tooltip>
        ),
        dialog: (
            <GridBulkEditDialog
                key={opening}
                open={open}
                count={count}
                fields={fields}
                labelId={labelId}
                titleId={titleId}
                // Read afresh at both moments: a record may have been ticked or
                // synced away since the dialog opened.
                validate={(values) =>
                    bulkFieldIssues(
                        schema,
                        inlineStoredValues(fields, values),
                        selectedDocs()
                    )
                }
                onCancel={() => setOpen(false)}
                onApply={async (values) => {
                    const docs = selectedDocs();
                    const result = await applyBulk(
                        docs.map((doc) => doc.id),
                        values
                    );
                    setOpen(false);
                    notify(
                        <>
                            <FormattedMessage
                                id={doneId}
                                values={{ count: result.changed }}
                            />
                            {result.missing > 0 && (
                                <>
                                    {' '}
                                    <FormattedMessage
                                        id="grid.bulkEdit.missing"
                                        values={{ count: result.missing }}
                                    />
                                </>
                            )}
                        </>,
                        result.missing > 0 ? 'warning' : 'success'
                    );
                    // The rows keep their ticks otherwise, and a second
                    // Übernehmen would write over the same records again.
                    apiRef.current?.setRowSelectionModel({
                        type: 'include',
                        ids: new Set()
                    });
                    setCount(0);
                }}
            />
        )
    };
}
